import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from '../lib/toast';
import Sidebar from '../components/layout/Sidebar';
import { Plus, Search, Trash2, Edit2, Package2, X, AlertTriangle, TrendingUp, TrendingDown, Tag, ShieldAlert, Building2 } from 'lucide-react';

// Simple Modal Component
const Modal = ({ open, onClose, title, children }) => {
  if (!open) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-background rounded-lg shadow-xl w-full max-w-md mx-4 p-6 z-10 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-chivo font-extrabold">{title}</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        {children}
      </div>
    </div>
  );
};

const UNIT_OPTIONS = [
  { value: 'kg', label: 'Kilogramo (kg)' },
  { value: 'g', label: 'Gramo (g)' },
  { value: 'lt', label: 'Litro (lt)' },
  { value: 'ml', label: 'Mililitro (ml)' },
  { value: 'pza', label: 'Pieza (pza)' },
  { value: 'unidad', label: 'Unidad' },
  { value: 'porcion', label: 'Porción' },
  { value: 'taza', label: 'Taza' },
  { value: 'cucharada', label: 'Cucharada' },
];

// Suggested categories for autocomplete
const SUGGESTED_CATEGORIES = [
  'Mariscos',
  'Verduras',
  'Frutas',
  'Abarrotes',
  'Lácteos',
  'Carnes',
  'Bebidas',
  'Refrescos',
  'Plásticos',
  'Desechables',
  'Limpieza',
  'Condimentos',
  'Aceites',
  'Especias'
];

const Supplies = () => {
  const { api } = useAuth();
  const { currentBranch, isMatriz, branchName } = useBranch();
  const [supplies, setSupplies] = useState([]);
  const [lowStockSupplies, setLowStockSupplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [existingCategories, setExistingCategories] = useState([]);
  
  // Supply Dialog
  const [supplyDialogOpen, setSupplyDialogOpen] = useState(false);
  const [editingSupply, setEditingSupply] = useState(null);
  const [supplyForm, setSupplyForm] = useState({
    name: '',
    category: '',
    unit: 'kg',
    cost_per_unit: '',
    stock: '',
    min_stock: '',
    supplier: '',
    is_consumable: false
  });
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);

  // Movement Dialog
  const [movementDialogOpen, setMovementDialogOpen] = useState(false);
  const [movementForm, setMovementForm] = useState({
    supply_id: '',
    quantity: '',
    movement_type: 'in',
    reason: ''
  });

  // Adjustment Dialog
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [adjustmentForm, setAdjustmentForm] = useState({
    supply: null,
    adjustment_type: 'merma',
    quantity: '',
    reason: ''
  });

  const ADJUSTMENT_TYPES = [
    { id: 'merma', name: 'Merma', color: 'text-orange-500', description: 'Producto dañado o en mal estado' },
    { id: 'robo', name: 'Robo', color: 'text-red-600', description: 'Producto extraviado o robado' },
    { id: 'vencido', name: 'Vencido', color: 'text-amber-500', description: 'Producto caducado' },
    { id: 'ajuste', name: 'Ajuste (+/-)', color: 'text-blue-500', description: 'Corrección de inventario' },
    { id: 'conteo_fisico', name: 'Conteo Físico', color: 'text-purple-500', description: 'Diferencia en conteo' }
  ];

  useEffect(() => {
    fetchData();
  }, [currentBranch]); // Reload when branch changes

  const fetchData = async () => {
    try {
      // Use optimized single endpoint
      const branchParams = currentBranch 
        ? `?branch_id=${currentBranch.branch_id}&is_matriz=${isMatriz}` 
        : '';
      
      const res = await api.get(`/supplies/init-data${branchParams}`);
      const data = res.data;
      
      setSupplies(data.supplies);
      setLowStockSupplies(data.low_stock);
      setExistingCategories(data.categories || []);
    } catch (error) {
      toast.error('Error al cargar insumos');
    } finally {
      setLoading(false);
    }
  };

  // Get all unique categories (existing + suggested)
  const getAllCategories = () => {
    const all = [...new Set([...existingCategories, ...SUGGESTED_CATEGORIES])];
    return all.sort();
  };

  // Filter categories for autocomplete
  const getFilteredCategories = () => {
    const all = getAllCategories();
    if (!supplyForm.category) return all;
    return all.filter(cat => 
      cat.toLowerCase().includes(supplyForm.category.toLowerCase())
    );
  };

  const handleSupplySubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!supplyForm.name?.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    
    const costValue = parseFloat(supplyForm.cost_per_unit);
    if (isNaN(costValue) || costValue < 0) {
      toast.error('El costo por unidad debe ser un número válido');
      return;
    }
    
    setSaving(true);
    
    try {
      const data = {
        name: supplyForm.name.trim(),
        category: supplyForm.category?.trim() || null,
        unit: supplyForm.unit,
        cost_per_unit: costValue,
        stock: parseFloat(supplyForm.stock) || 0,
        min_stock: parseFloat(supplyForm.min_stock) || 0,
        is_consumable: supplyForm.is_consumable || false,
        branch_id: editingSupply ? editingSupply.branch_id : (isMatriz ? null : currentBranch?.branch_id)
      };

      if (editingSupply) {
        const response = await api.put(`/supplies/${editingSupply.supply_id}`, data);
        // Actualizar localmente sin recargar todo
        setSupplies(prev => prev.map(s => 
          s.supply_id === editingSupply.supply_id ? { ...s, ...response.data } : s
        ));
        toast.success('Insumo actualizado');
      } else {
        const response = await api.post('/supplies', data);
        // Agregar localmente
        setSupplies(prev => [response.data, ...prev]);
        toast.success('Insumo creado');
      }
      
      setSupplyDialogOpen(false);
      resetSupplyForm();
      // Recargar en segundo plano para sincronizar
      fetchData();
    } catch (error) {
      console.error('Error guardando insumo:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar insumo');
    } finally {
      setSaving(false);
    }
  };

  const handleEditSupply = (supply) => {
    setEditingSupply(supply);
    setSupplyForm({
      name: supply.name || '',
      category: supply.category || '',
      unit: supply.unit || 'kg',
      cost_per_unit: (supply.cost_per_unit ?? 0).toString(),
      stock: (supply.stock ?? 0).toString(),
      min_stock: (supply.min_stock ?? 0).toString(),
      supplier: supply.supplier || '',
      is_consumable: supply.is_consumable || false
    });
    setSupplyDialogOpen(true);
  };

  const handleDeleteSupply = async (supplyId) => {
    if (!window.confirm('¿Eliminar este insumo?')) return;
    try {
      await api.delete(`/supplies/${supplyId}`);
      toast.success('Insumo eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar insumo');
    }
  };

  const resetSupplyForm = () => {
    setEditingSupply(null);
    setSupplyForm({
      name: '',
      category: '',
      unit: 'kg',
      cost_per_unit: '',
      stock: '',
      min_stock: '',
      supplier: '',
      is_consumable: false
    });
    setShowCategorySuggestions(false);
  };

  const handleMovementSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/supplies/movement', {
        supply_id: movementForm.supply_id,
        quantity: parseFloat(movementForm.quantity),
        movement_type: movementForm.movement_type,
        reason: movementForm.reason || null
      });
      toast.success('Movimiento registrado');
      setMovementDialogOpen(false);
      setMovementForm({ supply_id: '', quantity: '', movement_type: 'in', reason: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar movimiento');
    }
  };

  const openMovementDialog = (supply, type) => {
    setMovementForm({
      supply_id: supply.supply_id,
      quantity: '',
      movement_type: type,
      reason: ''
    });
    setMovementDialogOpen(true);
  };

  // Adjustment functions
  const openAdjustmentDialog = (supply) => {
    setAdjustmentForm({
      supply: supply,
      adjustment_type: 'merma',
      quantity: '',
      reason: ''
    });
    setAdjustmentDialogOpen(true);
  };

  const handleAdjustmentSubmit = async (e) => {
    e.preventDefault();
    const supply = adjustmentForm.supply;
    if (!supply) return;

    const qty = parseFloat(adjustmentForm.quantity);
    if (isNaN(qty) || qty <= 0) {
      toast.error('Ingrese una cantidad válida');
      return;
    }

    // For merma, robo, vencido - quantity is always negative (reducing stock)
    // For ajuste - can be positive or negative based on what user enters
    let finalQuantity = -Math.abs(qty); // Default: reduce stock
    if (adjustmentForm.adjustment_type === 'ajuste') {
      // For "ajuste" type, ask if it's adding or subtracting via the reason
      // We'll use negative by default, user can specify in reason
      finalQuantity = -Math.abs(qty);
    }

    try {
      await api.post('/stock-adjustments', {
        supply_id: supply.supply_id,
        item_type: 'supply',
        item_name: supply.name,
        adjustment_type: adjustmentForm.adjustment_type,
        quantity: finalQuantity,
        unit: supply.unit,
        reason: adjustmentForm.reason,
        cost_per_unit: supply.cost_per_unit || 0
      });
      toast.success('Ajuste registrado correctamente');
      setAdjustmentDialogOpen(false);
      setAdjustmentForm({ supply: null, adjustment_type: 'merma', quantity: '', reason: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar ajuste');
    }
  };

  // Filter supplies by search and category
  const filteredSupplies = supplies.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !categoryFilter || s.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Get unique categories from current supplies
  const supplyCategoriesInUse = [...new Set(supplies.map(s => s.category).filter(Boolean))];

  const getUnitLabel = (unit) => {
    return UNIT_OPTIONS.find(u => u.value === unit)?.label || unit;
  };

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-3 md:p-8 pt-16 md:pt-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-chivo font-extrabold" data-testid="supplies-title">
                Insumos
              </h1>
              <p className="text-muted-foreground text-sm">{supplies.length} insumos en {supplyCategoriesInUse.length} categorías</p>
            </div>
            
            <Button 
              className="bg-accent text-white hover:bg-accent/90 w-full sm:w-auto"
              onClick={() => {
                resetSupplyForm();
                setSupplyDialogOpen(true);
              }}
              data-testid="add-supply-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Insumo
            </Button>
          </div>

          {/* Low Stock Alert */}
          {lowStockSupplies.length > 0 && (
            <div className="mb-6 bg-white border border-red-200 rounded-xl shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-red-600 to-red-500 text-white px-4 py-3 flex items-center justify-between">
                <h2 className="font-bold flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Stock Bajo
                </h2>
                <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-semibold">
                  {lowStockSupplies.length} insumos
                </span>
              </div>
              <div className="max-h-52 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-red-50 sticky top-0">
                    <tr className="text-xs font-semibold text-red-800 uppercase">
                      <th className="px-4 py-2 text-left">Insumo</th>
                      <th className="px-4 py-2 text-left">Categoría</th>
                      <th className="px-4 py-2 text-right">Stock</th>
                      <th className="px-4 py-2 text-right">Mínimo</th>
                      <th className="px-4 py-2 text-center">Acción</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-red-100">
                    {lowStockSupplies.map(supply => (
                      <tr key={supply.supply_id} className="hover:bg-red-50 transition-colors">
                        <td className="px-4 py-2 font-medium text-gray-900">{supply.name}</td>
                        <td className="px-4 py-2 text-gray-600">{supply.category || '-'}</td>
                        <td className="px-4 py-2 text-right font-bold text-red-600">{supply.stock} {supply.unit}</td>
                        <td className="px-4 py-2 text-right text-gray-500">{supply.min_stock} {supply.unit}</td>
                        <td className="px-4 py-2 text-center">
                          <button
                            className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white text-xs font-medium rounded transition-colors"
                            onClick={() => openMovementDialog(supply, 'in')}
                          >
                            + Entrada
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Two Panel Layout */}
          <div className="flex gap-6">
            {/* Left Panel - Categories */}
            <div className="w-64 flex-shrink-0 hidden md:block">
              <div className="sticky top-4 bg-white border rounded-xl shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white p-4">
                  <h2 className="font-bold flex items-center gap-2">
                    <Tag className="w-5 h-5" />
                    Categorías
                  </h2>
                </div>
                <div className="p-2 max-h-[calc(100vh-280px)] overflow-y-auto">
                  {/* All Supplies */}
                  <button
                    className={`w-full text-left px-4 py-3 rounded-lg mb-1 transition-all flex items-center justify-between ${
                      categoryFilter === '' 
                        ? 'bg-accent text-white font-semibold shadow-md' 
                        : 'hover:bg-gray-100'
                    }`}
                    onClick={() => setCategoryFilter('')}
                  >
                    <span className="flex items-center gap-2">
                      <Package2 className="w-4 h-4" />
                      Todos
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${categoryFilter === '' ? 'bg-white/20' : 'bg-gray-200'}`}>
                      {supplies.length}
                    </span>
                  </button>
                  
                  <div className="border-t my-2"></div>
                  
                  {/* Categories */}
                  {supplyCategoriesInUse.sort().map(cat => {
                    const catCount = supplies.filter(s => s.category === cat).length;
                    const isSelected = categoryFilter === cat;
                    
                    return (
                      <button
                        key={cat}
                        className={`w-full text-left px-4 py-2.5 rounded-lg mb-1 transition-all flex items-center justify-between ${
                          isSelected 
                            ? 'bg-accent text-white font-semibold shadow-md' 
                            : 'hover:bg-gray-100'
                        }`}
                        onClick={() => setCategoryFilter(cat)}
                      >
                        <span className="truncate max-w-[140px]">{cat}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${isSelected ? 'bg-white/20' : 'bg-gray-200'}`}>
                          {catCount}
                        </span>
                      </button>
                    );
                  })}
                  
                  {supplyCategoriesInUse.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">Sin categorías</p>
                  )}
                </div>
              </div>
            </div>

            {/* Right Panel - Supplies Table */}
            <div className="flex-1 min-w-0">
              {/* Search Bar */}
              <div className="flex items-center gap-4 mb-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar insumos..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="search-supplies"
                  />
                </div>
                <div className="text-sm text-muted-foreground">
                  <span className="font-semibold text-foreground">{filteredSupplies.length}</span> insumos
                  {categoryFilter && (
                    <span> en <span className="font-medium">{categoryFilter}</span></span>
                  )}
                </div>
                
                {/* Mobile Category Filter */}
                <div className="md:hidden">
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Todas</option>
                    {supplyCategoriesInUse.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Supplies Table */}
              <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
                {/* Table Header */}
                <div className="bg-gray-50 border-b px-4 py-3 grid grid-cols-12 gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  <div className="col-span-3">Insumo</div>
                  <div className="col-span-2">Categoría</div>
                  <div className="col-span-1 text-center">Unidad</div>
                  <div className="col-span-2 text-right">Costo</div>
                  <div className="col-span-2 text-right">Stock</div>
                  <div className="col-span-2 text-center">Acciones</div>
                </div>
                
                {/* Table Body */}
                <div className="divide-y max-h-[calc(100vh-320px)] overflow-y-auto">
                  {filteredSupplies.map(supply => {
                    const isLowStock = supply.stock <= supply.min_stock;
                    
                    return (
                      <div 
                        key={supply.supply_id}
                        className={`px-4 py-3 grid grid-cols-12 gap-2 items-center hover:bg-gray-50 transition-colors ${isLowStock ? 'bg-red-50' : ''}`}
                      >
                        {/* Name */}
                        <div className="col-span-3">
                          <span className="font-semibold text-gray-900">{supply.name}</span>
                          {supply.is_consumable && (
                            <span className="ml-2 px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                              Consumible
                            </span>
                          )}
                        </div>
                        
                        {/* Category */}
                        <div className="col-span-2">
                          {supply.category ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent">
                              {supply.category}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                        
                        {/* Unit */}
                        <div className="col-span-1 text-center text-gray-600">
                          {supply.unit}
                        </div>
                        
                        {/* Cost */}
                        <div className="col-span-2 text-right font-medium">
                          ${supply.cost_per_unit.toFixed(2)}
                        </div>
                        
                        {/* Stock */}
                        <div className="col-span-2 text-right">
                          <span className={`font-semibold ${isLowStock ? 'text-red-600' : 'text-gray-900'}`}>
                            {supply.stock}
                          </span>
                          <span className="text-gray-500 text-sm ml-1">/ {supply.min_stock}</span>
                        </div>
                        
                        {/* Actions */}
                        <div className="col-span-2 flex items-center justify-center gap-1">
                          <button
                            className="p-1.5 hover:bg-green-100 rounded-lg transition-colors"
                            onClick={() => openMovementDialog(supply, 'in')}
                            title="Entrada"
                          >
                            <TrendingUp className="w-4 h-4 text-green-600" />
                          </button>
                          <button
                            className="p-1.5 hover:bg-red-100 rounded-lg transition-colors"
                            onClick={() => openMovementDialog(supply, 'out')}
                            title="Salida"
                          >
                            <TrendingDown className="w-4 h-4 text-red-500" />
                          </button>
                          <button
                            className="p-1.5 hover:bg-orange-100 rounded-lg transition-colors"
                            onClick={() => openAdjustmentDialog(supply)}
                            title="Ajuste"
                            data-testid={`adjust-${supply.supply_id}`}
                          >
                            <ShieldAlert className="w-4 h-4 text-orange-500" />
                          </button>
                          <button
                            className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                            onClick={() => handleEditSupply(supply)}
                            title="Editar"
                          >
                            <Edit2 className="w-4 h-4 text-gray-600" />
                          </button>
                          <button
                            className="p-1.5 hover:bg-red-100 rounded-lg transition-colors"
                            onClick={() => handleDeleteSupply(supply.supply_id)}
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Empty State */}
                {filteredSupplies.length === 0 && !loading && (
                  <div className="py-12 text-center">
                    <Package2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-500">No hay insumos en esta categoría</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Supply Modal */}
      <Modal
        open={supplyDialogOpen}
        onClose={() => {
          setSupplyDialogOpen(false);
          resetSupplyForm();
        }}
        title={editingSupply ? 'Editar Insumo' : 'Nuevo Insumo'}
      >
        <form onSubmit={handleSupplySubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Nombre del Insumo *</Label>
            <Input
              value={supplyForm.name}
              onChange={(e) => setSupplyForm({ ...supplyForm, name: e.target.value })}
              placeholder="Ej: Camarón, Limón, Aceite..."
              required
              data-testid="supply-name-input"
            />
          </div>

          {/* Category with autocomplete */}
          <div className="space-y-2 relative">
            <Label>Categoría</Label>
            <div className="relative">
              <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={supplyForm.category}
                onChange={(e) => {
                  setSupplyForm({ ...supplyForm, category: e.target.value });
                  setShowCategorySuggestions(true);
                }}
                onFocus={() => setShowCategorySuggestions(true)}
                onBlur={() => setTimeout(() => setShowCategorySuggestions(false), 200)}
                placeholder="Ej: Mariscos, Verduras, Abarrotes..."
                className="pl-10"
                data-testid="supply-category-input"
              />
            </div>
            
            {/* Category suggestions dropdown */}
            {showCategorySuggestions && getFilteredCategories().length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-48 overflow-y-auto">
                {getFilteredCategories().map(cat => (
                  <div
                    key={cat}
                    className="px-3 py-2 hover:bg-muted cursor-pointer text-sm"
                    onMouseDown={() => {
                      setSupplyForm({ ...supplyForm, category: cat });
                      setShowCategorySuggestions(false);
                    }}
                  >
                    {cat}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Unidad de Medida *</Label>
              <select
                value={supplyForm.unit}
                onChange={(e) => setSupplyForm({ ...supplyForm, unit: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="supply-unit-select"
              >
                {UNIT_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Costo por Unidad ($) *</Label>
              <Input
                type="number"
                step="0.01"
                value={supplyForm.cost_per_unit}
                onChange={(e) => setSupplyForm({ ...supplyForm, cost_per_unit: e.target.value })}
                required
                data-testid="supply-cost-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Stock Actual</Label>
              <Input
                type="number"
                step="0.01"
                value={supplyForm.stock}
                onChange={(e) => setSupplyForm({ ...supplyForm, stock: e.target.value })}
                data-testid="supply-stock-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Stock Mínimo</Label>
              <Input
                type="number"
                step="0.01"
                value={supplyForm.min_stock}
                onChange={(e) => setSupplyForm({ ...supplyForm, min_stock: e.target.value })}
                placeholder="Alerta cuando baje de..."
              />
            </div>
          </div>

          {/* Consumable Checkbox */}
          <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <input
              type="checkbox"
              id="is_consumable"
              checked={supplyForm.is_consumable}
              onChange={(e) => setSupplyForm({ ...supplyForm, is_consumable: e.target.checked })}
              className="w-5 h-5 rounded border-amber-300 text-amber-500 focus:ring-amber-500"
            />
            <div>
              <Label htmlFor="is_consumable" className="font-medium cursor-pointer">
                Es Consumible
              </Label>
              <p className="text-xs text-muted-foreground">
                Los consumibles (servilletas, bolsas, etc.) se registran como gasto directo al traspasar a un área, sin necesidad de receta.
              </p>
            </div>
          </div>

          <Button 
            type="submit" 
            className="w-full bg-accent text-white hover:bg-accent/90" 
            data-testid="save-supply-btn"
            disabled={saving}
          >
            {saving ? 'Guardando...' : (editingSupply ? 'Actualizar' : 'Crear')} {!saving && 'Insumo'}
          </Button>
        </form>
      </Modal>

      {/* Movement Modal */}
      <Modal
        open={movementDialogOpen}
        onClose={() => setMovementDialogOpen(false)}
        title={movementForm.movement_type === 'in' ? 'Entrada de Insumo' : 'Salida de Insumo'}
      >
        <form onSubmit={handleMovementSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Cantidad</Label>
            <Input
              type="number"
              step="0.01"
              value={movementForm.quantity}
              onChange={(e) => setMovementForm({ ...movementForm, quantity: e.target.value })}
              placeholder={`Cantidad en ${supplies.find(s => s.supply_id === movementForm.supply_id)?.unit || 'unidades'}`}
              required
              data-testid="movement-quantity-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Razón (opcional)</Label>
            <Input
              value={movementForm.reason}
              onChange={(e) => setMovementForm({ ...movementForm, reason: e.target.value })}
              placeholder={movementForm.movement_type === 'in' ? 'Compra a proveedor...' : 'Merma, uso...'}
            />
          </div>
          <Button type="submit" className={`w-full ${movementForm.movement_type === 'in' ? 'bg-success hover:bg-success/90' : 'bg-destructive hover:bg-destructive/90'} text-white`}>
            Registrar {movementForm.movement_type === 'in' ? 'Entrada' : 'Salida'}
          </Button>
        </form>
      </Modal>

      {/* Adjustment Modal */}
      <Modal
        open={adjustmentDialogOpen}
        onClose={() => setAdjustmentDialogOpen(false)}
        title="Ajuste de Inventario"
      >
        {adjustmentForm.supply && (
          <form onSubmit={handleAdjustmentSubmit} className="space-y-4">
            {/* Product Info */}
            <div className="p-3 bg-gray-100 rounded-lg">
              <p className="font-bold">{adjustmentForm.supply.name}</p>
              <p className="text-sm text-muted-foreground">
                Stock actual: <span className="font-medium">{adjustmentForm.supply.current_stock} {adjustmentForm.supply.unit}</span>
              </p>
              <p className="text-sm text-muted-foreground">
                Costo por {adjustmentForm.supply.unit}: <span className="font-medium">${adjustmentForm.supply.cost_per_unit?.toFixed(2) || '0.00'}</span>
              </p>
            </div>

            {/* Adjustment Type */}
            <div className="space-y-2">
              <Label>Tipo de Ajuste *</Label>
              <div className="grid grid-cols-2 gap-2">
                {ADJUSTMENT_TYPES.map(type => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setAdjustmentForm({...adjustmentForm, adjustment_type: type.id})}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      adjustmentForm.adjustment_type === type.id 
                        ? 'border-accent bg-accent/10' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className={`font-medium ${type.color}`}>{type.name}</span>
                    <p className="text-xs text-muted-foreground">{type.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div className="space-y-2">
              <Label>Cantidad a {adjustmentForm.adjustment_type === 'ajuste' ? 'ajustar' : 'restar'} *</Label>
              <div className="flex items-center gap-2">
                <span className="text-red-500 font-bold text-xl">-</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={adjustmentForm.quantity}
                  onChange={(e) => setAdjustmentForm({...adjustmentForm, quantity: e.target.value})}
                  placeholder="0.00"
                  required
                  className="flex-1"
                  data-testid="adjustment-quantity-input"
                />
                <span className="text-muted-foreground">{adjustmentForm.supply.unit}</span>
              </div>
              {adjustmentForm.quantity && (
                <p className="text-sm text-red-500">
                  Costo de pérdida: ${(parseFloat(adjustmentForm.quantity || 0) * (adjustmentForm.supply.cost_per_unit || 0)).toFixed(2)}
                </p>
              )}
            </div>

            {/* Reason */}
            <div className="space-y-2">
              <Label>Motivo / Observaciones *</Label>
              <Input
                value={adjustmentForm.reason}
                onChange={(e) => setAdjustmentForm({...adjustmentForm, reason: e.target.value})}
                placeholder="Ej: Producto en mal estado, caducado, etc."
                required
                data-testid="adjustment-reason-input"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="submit-adjustment-btn"
            >
              Registrar Ajuste
            </Button>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default Supplies;
